import { useCallback } from 'react';
import { collection, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'; // ADDED: Firestore imports
import { db } from '../../../firebase'; // ADJUST: Path to your firebase.ts
import { KanbanTask, Role, PermissionFlags, AppAction, TaskScope, DataScope, RecurrenceRangeType, TaskStatus } from '@/types'; // ADDED: AppAction, TaskScope, DataScope, RecurrenceRangeType, TaskStatus
import { addDays, addWeeks, addMonths, addYears, parseISO, isValid, differenceInDays } from 'date-fns'; // ADDED: parseISO, isValid

/**
 * 🛰️ RECURRENCE ENGINE v2.0
 * Refactored for Hybrid Scoping: Handles temporal rhythms across
 * Personal Partition and Shared Household Hub.
 */
export const useRecurrence = (
  currentUserId: string,
  currentHouseholdId: string | null,
  dispatch: React.Dispatch<AppAction>, // NEW: App dispatch function
  activeDataScope: DataScope, // NEW: User's active data scope (if needed for internal logic or child hooks)
  userPermissions?: PermissionFlags
) => {

  // NEW HELPER: Determine the correct Firestore collection reference
  const getTaskCollectionRef = useCallback((taskScope: TaskScope) => {
    // If it's a personal task OR no household is active, always use personalTasks
    if (taskScope === 'personal' || !currentHouseholdId) {
      return collection(db, 'users', currentUserId, 'personalTasks');
    }
    // Otherwise, if household scope and household is active, use household tasks
    return collection(db, 'households', currentHouseholdId, 'tasks');
  }, [currentUserId, currentHouseholdId]);

  /**
   * 📐 DATE MATH CORE
   */
  const calculateNextDueDate = useCallback((task: KanbanTask): string => {
    const config = task.recurrence;

    if (!config || !config.isEnabled) {
      return new Date().toISOString(); // Fallback if recurrence is not enabled
    }

    // Use lastResetDate or completedAt as the base, otherwise current date
    const baseDateStr = config.lastResetDate || task.completedAt || new Date().toISOString();
    const baseDate = parseISO(baseDateStr);
    const validBaseDate = isValid(baseDate) ? baseDate : new Date(); // Fallback to current date if parsing fails

    const interval = config.interval || 1;

    switch (config.frequency) {
      case 'days':
        return addDays(validBaseDate, interval).toISOString();
      case 'weeks':
        return addWeeks(validBaseDate, interval).toISOString();
      case 'months':
        return addMonths(validBaseDate, interval).toISOString();
      case 'years':
        return addYears(validBaseDate, interval).toISOString();
      default:
        // If frequency is null or unknown, default to adding 1 day
        console.warn(`Unknown or null recurrence frequency for task ${task.id}. Defaulting to daily.`);
        return addDays(validBaseDate, 1).toISOString();
    }
  }, []); // No external dependencies, task is passed in

  /**
   * ♻️ SUCCESSOR GENERATOR
   * Respects Google Data Privacy policies by strictly maintaining data siloing.
   */
  const processTaskRecurrence = useCallback(async (originalTask: KanbanTask): Promise<KanbanTask | null> => { // Made async
    if (!originalTask.recurrence || !originalTask.recurrence.isEnabled) {
      return null; // Return null as no recurring task will be created
    }

    /**
     * 🛡️ PERMISSION CHECK (Hybrid Scoping)
     * If the task is a Household task, verify the user has permission to manage it.
     */
    if (originalTask.scope === 'household' && currentHouseholdId && userPermissions) {
      const canManage = userPermissions.canManageTasks; // Assuming canManageTasks covers recurrence
      if (!canManage) {
        console.warn("Recurrence Blocked: Insufficient household permissions for task:", originalTask.id);
        return null; // Return null if unauthorized
      }
    }

    const nextDue = calculateNextDueDate(originalTask);

    // 🚀 CLONE & INITIALIZE SUCCESSOR WITH SCOPE AWARENESS
    const nextInstance: KanbanTask = {
      ...originalTask,
      id: doc(getTaskCollectionRef(originalTask.scope)).id, // Generate a new ID for the new instance

      /**
       * 🛡️ DATA PARTITION ROUTING
       * Ensure the new instance inherits the privacy scope of its parent.
       */
      scope: originalTask.scope, // The new instance retains the scope of the original task
      ownerId: currentUserId, // The current user is the owner/creator of this new recurring instance
      assignedTo: currentUserId,

      status: 'todo' as TaskStatus, // Next occurrence starts as todo
      dueDate: nextDue,
      createdAt: new Date().toISOString(), // New creation timestamp for the recurring task
      completedAt: undefined, // Reset completion date
      actualTime: 0, // Reset actual time
      localChangeTimestamp: Date.now(), // Client-side timestamp
      lastModifiedBy: currentUserId, // Last modified by current user

      subtasks: originalTask.subtasks ? originalTask.subtasks.map(st => ({
        ...st,
        id: `sub-${Math.random().toString(36).substring(2, 9)}`, // New ID for subtasks
        isCompleted: false
      })) : undefined,

      consumption: originalTask.consumption ? {
        ...originalTask.consumption,
        remainingQuantity: originalTask.consumption.quantity || 1, // Reset quantity/stock
        stockLevel: 1
      } : undefined, // consumption is optional
      recurrence: {
        ...originalTask.recurrence,
        lastResetDate: new Date().toISOString() // Update last reset date for new instance
      }
    };

    try {
      const taskCollectionRef = getTaskCollectionRef(originalTask.scope);
      await setDoc(doc(taskCollectionRef, nextInstance.id), {
        ...nextInstance,
        createdAt: serverTimestamp(), // Use server timestamp for consistency
        localChangeTimestamp: serverTimestamp() // Use server timestamp for consistency
      });
      // Dispatch ADD_TASK to update global state immediately
      dispatch({ type: 'ADD_TASK', payload: nextInstance });

      return nextInstance;
    } catch (e) {
      console.error("Error processing task recurrence:", e);
      return null;
    }
  }, [currentUserId, currentHouseholdId, userPermissions, dispatch, getTaskCollectionRef, calculateNextDueDate]);

  return { processTaskRecurrence };
};